// iot-admin V2 — root.
//
// Mounted at /v2/* OUTSIDE classic's <Layout>, so V2 inherits none of its
// chrome, tenant switcher or permission-graded nav. It shares exactly one
// thing with classic: services/api.js. Everything else is its own.
//
// Styling is isolated two ways: tokens are scoped under .ggv2, and every
// component style is a *.module.css with hashed class names — classic ships
// ~16,600 lines of global CSS in the same bundle and must not reach in here.

import React, { useCallback, useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';

import Shell from './shell/Shell';
import CommandPalette from './shell/CommandPalette';
import Stub from './domains/Stub';
import Triage from './domains/fleet/Triage';
import Admission from './domains/fleet/Admission';
import Reconcile from './domains/fleet/Reconcile';
import Onboarding from './domains/fleet/Onboarding';
import Ledger from './domains/fleet/Ledger';
import Releases from './domains/fleet/Releases';
import Posture from './domains/fleet/Posture';
import Review from './domains/demand/Review';
import Revenue from './domains/insights/Revenue';
import Legal from './domains/trust/Legal';
import Kyb from './domains/trust/Kyb';
import Statements from './domains/payouts/Statements';
import Split from './domains/payouts/Split';
import Agreements from './domains/payouts/Agreements';
import Eligibility from './domains/payouts/Eligibility';
import Ownership from './domains/payouts/Ownership';
import Recon from './domains/payouts/Recon';
import Pivot from './domains/insights/Pivot';
import Screens from './domains/insights/Screens';
import Spread from './domains/insights/Spread';
import Gates from './domains/insights/Gates';
import Tenants from './domains/demand/Tenants';
import Leads from './domains/demand/Leads';
import Board from './domains/board/Board';
import KillBoard from './domains/pane/KillBoard';   // W7 OP-3: the Pane domain's index (D-III-44)
import Catalogue from './domains/pane/Catalogue';   // W7 OP-5
import PaneAssets from './domains/pane/Assets';     // W7 OP-5: the Catalogue's asset register (a hidden nav item)
import Partners from './domains/pane/Partners';   // W7 OP-6
import PaneFleet from './domains/pane/Fleet';     // W7 OP-8
import PaneMoney from './domains/pane/Money';     // W7 OP-10
import PanePilot from './domains/pane/Pilot';     // W7 OP-11
import PaneStrip from './domains/pane/Strip';     // W7 OP-14
import Interactions from './domains/pane/Interactions';             // 2026-10-31: how passengers use the pane — aggregates + the cab register
import InteractionsCab from './domains/pane/InteractionsCab';       // one cab's ride timeline
import InteractionsTenant from './domains/pane/InteractionsTenant'; // one tenant, narrowed

import './styles.css';

export default function V2App() {
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const close = useCallback(() => setPaletteOpen(false), []);

  return (
    <div className="ggv2">
      <Shell>
        <Routes>
          <Route index element={<Triage />} />
          <Route path="fleet" element={<Triage />} />
          <Route path="fleet/admission" element={<Admission />} />
          <Route path="fleet/reconcile" element={<Reconcile />} />
          <Route path="fleet/onboarding" element={<Onboarding />} />
          <Route path="fleet/device/:thingName" element={<Ledger />} />
          <Route path="fleet/releases" element={<Releases />} />
          <Route path="fleet/posture" element={<Posture />} />
          <Route path="demand" element={<Review />} />
          <Route path="demand/tenants" element={<Tenants />} />
          <Route path="demand/leads" element={<Leads />} />
          {/* Pane — the operator plane (Increment III, D-III-44). The kill board is the domain's index because it is the
              lever an operator opens in a hurry; everything else is a register, each routed in the commit that renders it. */}
          <Route path="pane" element={<KillBoard />} />
          <Route path="pane/catalogue" element={<Catalogue />} />
          <Route path="pane/assets" element={<PaneAssets />} />
          <Route path="pane/partners" element={<Partners />} />
          <Route path="pane/fleet" element={<PaneFleet />} />
          <Route path="pane/money" element={<PaneMoney />} />
          <Route path="pane/pilot" element={<PanePilot />} />
          <Route path="pane/strip" element={<PaneStrip />} />
          <Route path="pane/interactions" element={<Interactions view="overview" />} />
          <Route path="pane/interactions/cabs" element={<Interactions view="cabs" />} />
          <Route path="pane/interactions/cab/:thingName" element={<InteractionsCab />} />
          <Route path="pane/interactions/tenant/:tenantId" element={<InteractionsTenant />} />
          <Route path="insights" element={<Revenue />} />
          <Route path="insights/pivot" element={<Pivot />} />
          <Route path="insights/screens" element={<Screens />} />
          <Route path="insights/spread" element={<Spread />} />
          <Route path="insights/gates" element={<Gates />} />
          {/* Trust → the LEGAL plane. Deliberately not under payouts, where
              "Agreements" already means the commercial terms a payout bills
              against — two different things share that word and this console
              carries both. */}
          <Route path="trust/legal" element={<Legal />} />
          {/* Trust → the IDENTITY plane. Who a counterparty legally IS, on both
              sides of the market — the party we invoice and the party we pay. */}
          <Route path="trust/kyb" element={<Kyb />} />
          <Route path="payouts" element={<Statements />} />
          <Route path="payouts/split" element={<Split />} />
          <Route path="payouts/agreements" element={<Agreements />} />
          <Route path="payouts/eligibility" element={<Eligibility />} />
          <Route path="payouts/recon" element={<Recon />} />
          <Route path="payouts/ownership" element={<Ownership />} />
          {/* Board — the founder's reference surface for a room. Four screens
              over the same marts the Insights and Payouts screens read, split
              by the three regimes that change what a rupee means. */}
          <Route path="board" element={<Board screen="estate" />} />
          <Route path="board/money" element={<Board screen="money" />} />
          <Route path="board/demand" element={<Board screen="demand" />} />
          <Route path="board/cab" element={<Board screen="cab" />} />
          <Route path="board/split" element={<Board screen="split" />} />
          {/* Every other destination declared in shell/nav.js renders the
              honest stub: what it is for, which classic routes it takes
              over, and a link straight to them. */}
          <Route path="*" element={<Stub />} />
        </Routes>
      </Shell>
      <CommandPalette open={paletteOpen} onClose={close} />
    </div>
  );
}

export { V2App };
